import type { CategoryNode } from '@/api/address-book/types';
import { Permission, type PermissionGroupKey } from '@/api/permissions';

import { type CatalogGroup, type GetRoleRes, RoleScopeKind } from './types';

export const ADDRESS_BOOK_GROUP_KEY: PermissionGroupKey = 'address-book';
export const FULL_VIEW_PERMISSION = Permission.AddressBookViewAllCategories;

export const CATEGORY_SCOPE_TITLE = 'Categories';
export const CATEGORY_SCOPE_HELP =
  "Categories add up across a member's roles. A role with no categories opens nothing in the address book; a role with all categories sees everything.";
export const ALL_CATEGORIES_LABEL = 'All categories';
export const ALL_CATEGORIES_DESCRIPTION = 'The role sees every category and every contact.';
export const ALL_CATEGORIES_HELD_NOTE =
  'Already on. The role sees every category and every contact.';
export const ALL_CATEGORIES_LOCKED_ROLE_REASON =
  "A default role's permissions are fixed. Duplicate the role to give it all categories.";
export const GAMMA_ALL_CATEGORIES_REASON =
  "A Gamma role can't be given all categories by request. Duplicate GammaAdmin to start a role that sees all categories.";
export const REQUEST_ALL_CATEGORIES_LABEL = 'Request all categories';
export const ALL_CATEGORIES_REQUEST_HELP =
  'Giving a role all categories is a permission change. It goes through the role-change request flow like any other permission.';
export const ALL_CATEGORIES_REQUESTED_NOTE =
  'Requested. The role sees all categories once the change is applied.';
export const SELECTED_CATEGORIES_LABEL = 'Selected categories';
export const SELECTED_CATEGORIES_DESCRIPTION = 'The role opens only the categories picked below.';
export const SELECTED_CATEGORIES_LOCKED_ROLE_REASON =
  "A default role's permissions are fixed. Duplicate the role to limit its categories.";
export const SWITCH_TO_SELECTED_LABEL = 'Save and switch to selected categories';
export const SELECTED_REQUEST_HELP =
  'Turning all categories off is a permission change. It goes through the role-change request flow like any other permission.';
export const SELECTED_REQUESTED_NOTE =
  'Requested. The role opens only the selected categories once the change is applied.';
export const GAMMA_SWITCH_WARNING =
  "This can't be undone from the editor. A Gamma role gets all categories back only by duplicating GammaAdmin.";
export const CATEGORIES_READ_DENIED_REASON = 'You need Address Book access to pick categories.';
export const CATEGORIES_LOAD_ERROR = "Couldn't load categories";
export const NO_CATEGORIES_MESSAGE = 'This address book has no categories yet.';
export const SAVE_CATEGORIES_LABEL = 'Save categories';

export const includedWithLabel = (ancestorName: string) => `Included with ${ancestorName}`;

export const selectedCountLabel = (count: number) => {
  if (count === 0) return 'No categories selected. The role opens nothing in the address book.';

  return count === 1 ? '1 category selected' : `${count} categories selected`;
};

export const staleCategoriesWarning = (count: number) =>
  count === 1
    ? '1 saved category no longer exists and will be dropped when you save.'
    : `${count} saved categories no longer exist and will be dropped when you save.`;

export const staleCategoriesNote = (count: number) =>
  count === 1 ? '1 saved category no longer exists.' : `${count} saved categories no longer exist.`;

export type CategoryScopeState =
  | { mode: 'hidden' }
  | { mode: 'all'; canRequestSelected: boolean; savedIds: readonly string[] }
  | { mode: 'selected'; canRequestAll: boolean; savedIds: readonly string[] };

export const savedCategoryIds = (role: Pick<GetRoleRes, 'scopes'>): readonly string[] => {
  // The role detail served by APIs released before GP-4068 omits `scopes`; drop this fallback once
  // every environment's API returns them.
  const scopes = role.scopes ?? [];

  return (
    scopes.find((scope) => scope.kind === RoleScopeKind.AddressBookCategory)?.resourceIds ?? []
  );
};

export const resolveCategoryScopeState = ({
  role,
  scopable,
}: {
  role: GetRoleRes;
  scopable: boolean;
}): CategoryScopeState => {
  if (role.permissions.some((row) => row.permission === FULL_VIEW_PERMISSION)) {
    return {
      mode: 'all',
      canRequestSelected: scopable && !role.isLocked,
      savedIds: savedCategoryIds(role),
    };
  }
  if (!scopable) return { mode: 'hidden' };

  return { mode: 'selected', canRequestAll: !role.isLocked, savedIds: savedCategoryIds(role) };
};

export const withoutFullViewRow = (groups: readonly CatalogGroup[]): CatalogGroup[] =>
  groups.map((group) =>
    group.key === ADDRESS_BOOK_GROUP_KEY
      ? {
          ...group,
          permissions: group.permissions.filter(
            (entry) => entry.permission !== FULL_VIEW_PERMISSION,
          ),
        }
      : group,
  );

const walk = (
  nodes: readonly CategoryNode[],
  visit: (_node: CategoryNode, _ancestors: readonly CategoryNode[]) => void,
  ancestors: readonly CategoryNode[] = [],
) => {
  nodes.forEach((node) => {
    visit(node, ancestors);
    walk(node.children, visit, [...ancestors, node]);
  });
};

export const flattenCategoryIds = (categories: readonly CategoryNode[]): string[] => {
  const ids: string[] = [];
  walk(categories, (node) => ids.push(node.id));

  return ids;
};

export const splitStaleCategoryIds = (
  ids: readonly string[],
  categories: readonly CategoryNode[],
) => {
  const known = new Set(flattenCategoryIds(categories));

  return {
    known: ids.filter((id) => known.has(id)),
    stale: ids.filter((id) => !known.has(id)),
  };
};

export const coveredCategoryIds = (
  categories: readonly CategoryNode[],
  picked: ReadonlySet<string>,
): Map<string, string> => {
  const covered = new Map<string, string>();

  walk(categories, (node, ancestors) => {
    const nearest = [...ancestors].reverse().find((ancestor) => picked.has(ancestor.id));
    if (nearest) covered.set(node.id, nearest.name);
  });

  return covered;
};

const descendantIds = (categories: readonly CategoryNode[], id: string): Set<string> => {
  const ids = new Set<string>();

  walk(categories, (node, ancestors) => {
    if (ancestors.some((ancestor) => ancestor.id === id)) ids.add(node.id);
  });

  return ids;
};

export const toggleCategoryScope = (
  picked: readonly string[],
  categories: readonly CategoryNode[],
  id: string,
): string[] => {
  if (picked.includes(id)) return picked.filter((pickedId) => pickedId !== id);

  const descendants = descendantIds(categories, id);

  return [...picked.filter((pickedId) => !descendants.has(pickedId)), id];
};

export const toCategoryScopePayload = (
  picked: readonly string[],
  categories: readonly CategoryNode[],
): string[] => {
  const pickedSet = new Set(picked);

  return flattenCategoryIds(categories).filter((id) => pickedSet.has(id));
};

export const isCategoryScopeDirty = (
  saved: readonly string[],
  payload: readonly string[],
): boolean => {
  const savedSet = new Set(saved);

  return savedSet.size !== payload.length || payload.some((id) => !savedSet.has(id));
};
